import { ContentType } from '@/api/generated-api'
import type { TaskBrandResource } from '@/features/tasks/api/taskBrandResource'
import { withRequestId } from '@/lib/productAnalytics'
import { apiRequest } from '@/shared/api/client'

export type WatchTaskProgressState = 'in_progress' | 'success' | 'failed'

const VIDEO_URL_KEYS = [
  'video_url',
  'stream_url',
  'media_url',
  'playback_url',
  'hls_url',
  'url'
] as const

export function pickVideoUrl(source: unknown): string | undefined {
  if (!source || typeof source !== 'object') {
    return undefined
  }
  const o = source as Record<string, unknown>
  for (const key of VIDEO_URL_KEYS) {
    const v = o[key]
    if (typeof v === 'string' && v.trim().length > 0) {
      return v
    }
  }
  return undefined
}

export interface ReserveTaskProgressResponse {
  task_progress_id?: string
  [key: string]: unknown
}

export async function postTaskProgress(taskId: string, requestId?: string) {
  return apiRequest<ReserveTaskProgressResponse>({
    path: `/api/v2/tasks/${encodeURIComponent(taskId)}/progress`,
    method: 'POST',
    body: {},
    type: ContentType.Json,
    format: 'json',
    ...(requestId ? withRequestId(requestId) : {})
  })
}

export type ActionStorePlatform = 'app_store' | 'google_play' | 'universal'

export type ActionStoreLinkResponse = {
  url?: string
  state?: string
  attempt_expires_at?: string
}

export async function postActionStoreLink(
  taskId: string,
  platform: ActionStorePlatform
) {
  return apiRequest<ActionStoreLinkResponse>({
    path: `/api/v2/tasks/${encodeURIComponent(taskId)}/action/store-link`,
    method: 'POST',
    body: { platform },
    type: ContentType.Json,
    format: 'json'
  })
}

/** One step of an Action task: a brand event with its own reward and deadline. */
export type ActionTaskStep = {
  id?: string
  title?: string
  description?: string
  /** Optional long text of what the step asks for. */
  details?: string
  /** AP the fan gets for this step. */
  fan_reward?: number
  /** The other steps wait for it; only the first step can be main. */
  main?: boolean
  /** Hours for the step, counted from the reservation. */
  deadline_hours?: number
}

/** locked — waits for the main step; expired — its deadline passed undone. */
export type ActionAttemptStepStatus = 'locked' | 'pending' | 'done' | 'expired'

export type ActionAttemptStep = ActionTaskStep & {
  status?: ActionAttemptStepStatus
  completed_at?: string
  expires_at?: string
}

export type ActionAttemptResponse = {
  state?: string
  /** IN_PROGRESS not activated yet, VERIFYING activated with steps left. */
  ui_status?: string
  /** The gate deadline before activation, the last open step deadline after. */
  attempt_expires_at?: string
  steps?: ActionAttemptStep[]
  earned_fan_reward?: number
  total_fan_reward?: number
  failure_reason?: string
  platform?: string
}

export async function getActionAttempt(taskId: string) {
  return apiRequest<ActionAttemptResponse>({
    path: `/api/v2/tasks/${encodeURIComponent(taskId)}/action/attempt`,
    method: 'GET',
    format: 'json'
  })
}

export type CompleteWatchRequestBody = {
  total_seconds: number
  watched_seconds: number
}

export type CompleteWatchResponse = {
  state: WatchTaskProgressState
  completed: boolean
}

export function buildCompleteWatchBody(
  totalDurationSeconds: number,
  watchedSecondsRaw: number
): CompleteWatchRequestBody {
  const totalRounded = Math.round(
    Number.isFinite(totalDurationSeconds) ? totalDurationSeconds : 0
  )
  const total_seconds = Math.max(1, totalRounded)

  const watchedRounded = Math.round(
    Number.isFinite(watchedSecondsRaw) ? watchedSecondsRaw : 0
  )
  const watched_seconds = Math.min(total_seconds, Math.max(0, watchedRounded))

  return { total_seconds, watched_seconds }
}

export async function postCompleteWatch(
  taskProgressId: string,
  body: CompleteWatchRequestBody,
  requestId?: string
) {
  return apiRequest<CompleteWatchResponse>({
    path: `/api/v2/tasks/progress/${encodeURIComponent(
      taskProgressId
    )}/complete-watch`,
    method: 'POST',
    body,
    type: ContentType.Json,
    format: 'json',
    ...(requestId ? withRequestId(requestId) : {})
  })
}

export type MyTasksItemType = 'watch' | 'share' | 'action'

export type TaskListTypeFilter = 'watch' | 'share' | 'action' | 'create'

export type MyActiveTaskProgress = {
  task_id: string
  campaign_id: string
  campaign_name: string
  title?: string
  description?: string
  brand_name: string
  brand_logo_url?: string
  brand_resources?: TaskBrandResource[]
  type: MyTasksItemType | string
  fan_reward: number
  volume: number
  claimed_count: number
  target_views?: number
  available_until?: string
  instruction?: Record<string, unknown> | null
  platform?: string
  task_progress_id?: string
  id?: string
  state?: string
  expires_at?: string
  /** Personal views-collection deadline; set only for in_progress share rows. */
  views_deadline_at?: string
  started_at?: string
  completed_at?: string
  failure_message?: string
  failure_reason?: string
  submitted_url?: string
  thumbnail_url?: string
  reward_claimed?: boolean
  views_count?: number
  reference_video_url?: string
  post_template?: string
  action_details?: Record<string, unknown> | null
  is_frozen?: boolean
  frozen_until?: string
}

export type ClosedTaskProgressRow = MyActiveTaskProgress

export type ListTaskProgressesResponse = {
  task_progresses: MyActiveTaskProgress[]
  total: number
  /** Platforms of every row the request's filters admit, `platforms` aside (all pages). */
  available_platforms?: string[]
}

export type ListMyActiveTasksResponse = ListTaskProgressesResponse

export type ListClosedTasksResponse = ListTaskProgressesResponse

/** Server-side feed order; omitted = the list's default order. */
export type TaskFeedSort =
  | 'reward_desc'
  | 'reward_asc'
  | 'deadline_asc'
  | 'newest'
  | 'spots_left_asc'

/** The one status a card shows on My tasks. */
export type TaskCardStatusParam = 'action_required' | 'frozen' | 'in_progress'

/** Feed filters the task lists apply on the server. */
export type TaskFeedFilterParams = {
  /** Any-of: a task's social network, or a store of an action task. */
  platforms?: readonly string[]
  sort?: TaskFeedSort
  /** My lists only: cards showing this status. */
  card_status?: TaskCardStatusParam
}

export function appendTaskFeedFilter(
  qs: URLSearchParams,
  params: TaskFeedFilterParams | undefined
) {
  for (const platform of params?.platforms ?? []) {
    qs.append('platforms', platform)
  }
  if (params?.sort) {
    qs.set('sort', params.sort)
  }
  if (params?.card_status) {
    qs.set('card_status', params.card_status)
  }
}

export type GetMyTasksParams = TaskFeedFilterParams & {
  page?: number
  page_size?: number
  type?: TaskListTypeFilter
  /** Any-of type filter (ANDed with `type`). */
  types?: readonly TaskListTypeFilter[]
}

function buildTasksProgressListPath(
  basePath: '/api/v2/tasks/my' | '/api/v2/tasks/closed',
  params?: GetMyTasksParams
): string {
  const qs = new URLSearchParams()
  if (params?.page !== undefined) {
    qs.set('page', String(params.page))
  }
  if (params?.page_size !== undefined) {
    qs.set('page_size', String(params.page_size))
  }
  if (params?.type) {
    qs.set('type', params.type)
  }
  for (const type of params?.types ?? []) {
    qs.append('types', type)
  }
  appendTaskFeedFilter(qs, params)
  const suffix = qs.toString()
  return `${basePath}${suffix ? `?${suffix}` : ''}`
}

export async function getTasksMy(params?: GetMyTasksParams) {
  return apiRequest<ListTaskProgressesResponse>({
    path: buildTasksProgressListPath('/api/v2/tasks/my', params),
    method: 'GET',
    format: 'json'
  })
}

export type ActiveTabTaskProgressesPage = ListTaskProgressesResponse & {
  shareHasMore: boolean
  actionHasMore: boolean
  createHasMore: boolean
}

/** Fan Active tab: share, action and create; watch progress lives on the watch page. */
export async function getTasksMyForActiveTab(
  params: Required<Pick<GetMyTasksParams, 'page' | 'page_size'>>
): Promise<ActiveTabTaskProgressesPage> {
  const [shareRes, actionRes, createRes] = await Promise.all([
    getTasksMy({ ...params, type: 'share' }),
    getTasksMy({ ...params, type: 'action' }),
    getTasksMy({ ...params, type: 'create' })
  ])

  const pageEnd = params.page * params.page_size

  return {
    task_progresses: [
      ...shareRes.task_progresses,
      ...actionRes.task_progresses,
      ...createRes.task_progresses
    ],
    total: shareRes.total + actionRes.total + createRes.total,
    shareHasMore: pageEnd < shareRes.total,
    actionHasMore: pageEnd < actionRes.total,
    createHasMore: pageEnd < createRes.total
  }
}

export function getActiveTabTasksNextPageParam(
  lastPage: ActiveTabTaskProgressesPage,
  lastPageParam: number
): number | undefined {
  if (
    !lastPage.shareHasMore &&
    !lastPage.actionHasMore &&
    !lastPage.createHasMore
  ) {
    return undefined
  }
  return lastPageParam + 1
}

export async function getTasksClosed(params?: GetMyTasksParams) {
  return apiRequest<ListClosedTasksResponse>({
    path: buildTasksProgressListPath('/api/v2/tasks/closed', params),
    method: 'GET',
    format: 'json'
  })
}

export type AvailableTaskCard = {
  task_id: string
  campaign_id: string
  campaign_name?: string
  title?: string
  description?: string
  brand_name?: string
  brand_logo_url?: string
  brand_resources?: TaskBrandResource[]
  brand_id?: string
  type?: string
  state?: string
  is_retry?: boolean
  failure_reason?: string
  available_until?: string
  fan_reward?: number
  reward?: string | number
  volume?: number
  claimed_count?: number
  target_views?: number
  platform?: string
  instruction?: Record<string, unknown> | null
  video_url?: string
  thumbnail_url?: string
  reference_video_url?: string
  post_template?: string
  /** How long a reservation of the task lasts before it times out. */
  reservation_seconds?: number
  action_details?: Record<string, unknown> | null
}

export type ListAvailableTasksResponse = {
  tasks: AvailableTaskCard[]
  total: number
  /** Platforms of every task the request's filters admit, `platforms` aside (all pages). */
  available_platforms?: string[]
}

export type AvailableTasksStatus = 'ACTIVE' | 'SOON'

export async function getTasksAvailable(
  params: TaskFeedFilterParams & {
    page?: number
    page_size?: number
    type?: TaskListTypeFilter
    /** `false` — Available for start; `true` — Available to retry. */
    only_retry?: boolean
    /** Public catalog slice; omit/`ACTIVE` = reservable, `SOON` = scheduled preview. */
    status?: AvailableTasksStatus
  }
) {
  const qs = new URLSearchParams()
  if (params.page !== undefined) {
    qs.set('page', String(params.page))
  }
  if (params.page_size !== undefined) {
    qs.set('page_size', String(params.page_size))
  }
  if (params.type) {
    qs.set('type', params.type)
  }
  if (params.only_retry !== undefined) {
    qs.set('only_retry', String(params.only_retry))
  }
  if (params.status) {
    qs.set('status', params.status)
  }
  appendTaskFeedFilter(qs, params)
  const suffix = qs.toString()
  return apiRequest<ListAvailableTasksResponse>({
    path: `/api/v2/tasks/available${suffix ? `?${suffix}` : ''}`,
    method: 'GET',
    format: 'json'
  })
}
